/**
 * Keyboard shortcuts of the calendar (owner remark 28): as on a Mac, with ⌘ / ⌥; on Windows Ctrl instead.
 * Pure helpers; the events page reacts to them.
 */

export type Shortcut =
  | { kind: 'undo' }
  | { kind: 'copy' }
  | { kind: 'paste' }
  | { kind: 'duplicate' }
  | { kind: 'delete' }
  | { kind: 'preview' }
  | { kind: 'move-day'; days: number };

type Keys = { key: string; metaKey: boolean; ctrlKey: boolean; altKey: boolean; shiftKey: boolean };

export const isMacLike = (): boolean => typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

const ARROWS: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

/** The shortcut of a key press, or null. `mac`: ⌘ is the command key (else Ctrl). */
export function shortcutOf(e: Keys, mac: boolean): Shortcut | null {
  const command = mac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  const plain = !e.metaKey && !e.ctrlKey && !e.altKey;
  if (command && !e.altKey && !e.shiftKey) {
    const k = e.key.toLowerCase();
    if (k === 'z') return { kind: 'undo' };
    if (k === 'c') return { kind: 'copy' };
    if (k === 'v') return { kind: 'paste' };
    if (k === 'd') return { kind: 'duplicate' };
    return null;
  }
  if (!plain || e.shiftKey) return null;
  if (e.key === 'Backspace' || e.key === 'Delete') return { kind: 'delete' };
  if (e.key === ' ') return { kind: 'preview' };
  if (e.key in ARROWS) return { kind: 'move-day', days: ARROWS[e.key]! };
  return null;
}

/** Dropping an event copies it with ⌥ held (Mac) or Ctrl / Alt (Windows); otherwise it moves. */
export function copyModifier(e: { altKey: boolean; ctrlKey: boolean }, mac: boolean): boolean {
  return mac ? e.altKey : e.ctrlKey || e.altKey;
}

/**
 * Owner 2026-10-23 18:34 („jak klikam na pozycje w kosztorysie i delete to usuwa … wydarzenie w kalendarzu”): where the
 * keyboard was last taken — by a press or by the focus. A click on something in the card that takes no focus (a cell of
 * the quote, a text) leaves the focus on the body, so the target alone cannot tell; the zone can. A popup (dialog, menu,
 * hint) leaves it as it was, and so does a green notice at the bottom of the page (one of the card takes the card's
 * zone); so does the card's heading getting the focus (it does so by itself when an event's bar is
 * clicked: ⌫, ⌘C, ⌘D and space then still act on that event).
 */
export type KeyZone = 'calendar' | 'card';

const POPUP = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [role="tooltip"]';

export function keyZoneOf(target: EventTarget | null, current: KeyZone, how: 'press' | 'focus'): KeyZone {
  if (!(target instanceof Element) || target === document.body || target === document.documentElement) return current;
  if (target.closest(POPUP)) return current;
  // review I3: the green notices at the bottom of the page (ui/Notice.tsx) — one of the event card carries the card's
  // zone; the others leave the zone as it was
  const notice = target.closest('.notice-area');
  if (notice) return target.closest('[data-key-zone="card"]') ? 'card' : current;
  const panel = target.closest('.cal-panel');
  if (!panel) return 'calendar';
  if (how === 'focus' && target.tagName === 'H2' && target.parentElement?.closest('.cal-panel') === panel) return current;
  return 'card';
}

/**
 * Where shortcuts stay off: after the last press or focus in the event card (`zone`, see `keyZoneOf`), typing in a
 * field, a dialog or "…" menu, the event card (except its heading, where focus lands when the card opens — so ⌘C, ⌘D,
 * ⌫ and space work right after clicking an event) and the splitter (its arrows resize the card).
 */
export function ignoresShortcuts(target: EventTarget | null, zone: KeyZone = 'calendar'): boolean {
  if (zone === 'card') return true;
  if (!(target instanceof Element)) return false;
  if (target.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""], [role="separator"]')) return true;
  if (target.closest('[role="dialog"]:not(.cal-preview), [role="alertdialog"], [role="menu"], .notice-area')) return true;
  const panel = target.closest('.cal-panel');
  return panel !== null && !(target.tagName === 'H2' && target.parentElement?.closest('.cal-panel') === panel);
}
